import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  useGetBankTransactionsQuery,
  useUpdateTransactionMutation,
  BankTransaction,
} from '@/store/transactionsApi';
// import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import CategorySelector from '@/modules/cashFlow/components/CategorySelector';
import { formatNumberToCurrency } from '@/utils/currency';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { formatAppDate, parseOptionalDate } from '@/utils/date';

interface PositiveTransactionWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  startDate: string;
  endDate: string;
  investorId?: number;
  onRefreshStart: () => void;
  incomeCategoryId?: number;
  transferCategoryId?: number;
  isChaining?: boolean;
  chainProgress?: { current: number; total: number };
}

interface TransactionStep {
  transaction: BankTransaction;
  action?: 'income' | 'transfer' | 'spending';
  categoryId?: number;
  alwaysChange?: boolean;
  skipped?: boolean;
}

const DIALOG_SX = { '& .MuiDialog-paper': { height: '90vh' } } as const;

const PositiveTransactionWizard: React.FC<PositiveTransactionWizardProps> = ({
  open,
  onClose,
  onComplete,
  startDate,
  endDate,
  investorId,
  onRefreshStart,
  // incomeCategoryId,
  transferCategoryId,
  isChaining = false,
  chainProgress,
}) => {
  const authLoaded = useAuthIsLoaded();

  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [steps, setSteps] = useState<TransactionStep[]>([]);
  const [currentStep, setCurrentStep] = useState<'action' | 'income'>('action');
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | undefined
  >();
  const [alwaysChange, setAlwaysChange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Queries - TODO: Using existing getBankTransactions, may need backend parameter for positive spending transactions
  const { data: transactionsData, isLoading: transactionsLoading } =
    useGetBankTransactionsQuery(
      authLoaded
        ? {
            startDate,
            endDate,
            OnlyUncategorized: true,
            hideTransfers: true,
            sort: { Property: 'TransactionDate', Direction: 'asc' },
            limit: 500,
            ...(typeof investorId === 'number'
              ? { InvestorId: investorId }
              : {}),
          }
        : skipToken,
    );

  // const { data: categoriesData } = useGetCashflowCategoriesQuery(
  //     authLoaded && typeof investorId === 'number' ? { InvestorId: investorId } : skipToken
  // );

  const [updateTransaction] = useUpdateTransactionMutation();

  // Filter positive spending transactions client-side
  const positiveSpendingTransactions = useMemo(() => {
    console.log(
      'Filter transaction data to get positive transactions clasified as spending ====>>>',
      transactionsData,
    );
    if (!transactionsData?.transactions) return [];

    // TODO: Verify this filtering logic matches backend requirements
    return transactionsData.transactions.filter((tx) => tx.Amount > 0);
  }, [transactionsData]);

  // Init steps on load
  useEffect(() => {
    if (positiveSpendingTransactions.length > 0) {
      const newSteps = positiveSpendingTransactions.map((transaction) => ({
        transaction,
      }));
      setSteps(newSteps);
      setCurrentIndex(0);
      setCurrentStep('action');
      setSelectedCategoryId(undefined);
      setAlwaysChange(false);
      setIsComplete(false);
    }
  }, [positiveSpendingTransactions]);

  const currentTransaction = steps[currentIndex]?.transaction;

  const resolvedTransactions = useMemo(() => {
    return steps.reduce(
      (count, step) => (step.action && !step.skipped ? count + 1 : count),
      0,
    );
  }, [steps]);

  const skippedCount = useMemo(
    () => steps.filter((s) => s.skipped).length,
    [steps],
  );

  const editedCount = useMemo(
    () => steps.filter((s) => !!s.action && !s.skipped).length,
    [steps],
  );

  const editedSteps = useMemo(
    () => steps.filter((s) => !!s.action && !s.skipped),
    [steps],
  );

  // Helpers
  const wipeMany = (indices: number[]) => {
    setSteps((prevSteps) => {
      const nextSteps = [...prevSteps];
      for (const idx of indices) {
        if (idx < 0 || idx >= nextSteps.length) continue;
        nextSteps[idx] = {
          ...nextSteps[idx],
          action: undefined,
          categoryId: undefined,
          alwaysChange: undefined,
          skipped: undefined,
        };
      }
      return nextSteps;
    });
  };

  // Actions
  const handleActionSelect = (action: 'income' | 'transfer' | 'spending') => {
    if (!currentTransaction) return;

    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        action,
        skipped: undefined,
        categoryId: action === 'transfer' ? transferCategoryId : undefined,
        alwaysChange: undefined,
      };
      return next;
    });

    if (action === 'income') {
      setCurrentStep('income');
      setSelectedCategoryId(undefined);
      setAlwaysChange(false);
    } else {
      // Transfer or Spending - move to next immediately
      moveToNext();
    }
  };

  const handleIncomeCategory = (categoryId: number) => {
    if (!currentTransaction) return;

    setSelectedCategoryId(categoryId);
    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        categoryId,
      };
      return next;
    });
  };

  const handleIncomeComplete = () => {
    if (!currentTransaction || selectedCategoryId === undefined) return;

    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        categoryId: selectedCategoryId,
        alwaysChange,
      };
      return next;
    });

    moveToNext();
  };

  const moveToNext = () => {
    setSelectedCategoryId(undefined);
    setAlwaysChange(false);
    setCurrentStep('action');

    if (currentIndex < steps.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handleSkip = () => {
    setSteps((prev) => {
      const next = [...prev];
      next[currentIndex] = {
        ...next[currentIndex],
        skipped: true,
        action: undefined,
        categoryId: undefined,
        alwaysChange: undefined,
      };
      return next;
    });

    moveToNext();
  };

  const moveBack = () => {
    if (isComplete) {
      setIsComplete(false);
      const lastIdx = steps.length - 1;
      wipeMany([lastIdx]);
      setCurrentIndex(lastIdx);
      setSelectedCategoryId(undefined);
      setCurrentStep('action');
      return;
    }

    if (currentStep === 'income') {
      // Go back to action selection for same transaction
      wipeMany([currentIndex]);
      setSelectedCategoryId(undefined);
      setAlwaysChange(false);
      setCurrentStep('action');
      return;
    }

    if (currentIndex > 0) {
      // Wipe current and previous to make "back then forward" explicit
      wipeMany([currentIndex, currentIndex - 1]);
      setSelectedCategoryId(undefined);
      setAlwaysChange(false);
      setCurrentIndex((i) => i - 1);
      setCurrentStep('action');
    }
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      const transactionsToUpdate = steps.filter((s) => s.action && !s.skipped);

      for (const step of transactionsToUpdate) {
        const tx = step.transaction;
        let categoryId = step.categoryId;

        // Determine final category ID based on action
        if (step.action === 'transfer') {
          categoryId = transferCategoryId;
        } else if (step.action === 'spending') {
          // Keep existing category (mark as correct)
          categoryId = tx.BankTransactionCategoryId;
        }

        if (!categoryId) continue;

        const transactionDate = parseOptionalDate(tx.TransactionDate);
        const payload: BankTransaction = {
          ...tx,
          WealthAccountTransactionId: tx.WealthAccountTransactionId,
          BankTransactionCategoryId: categoryId,
          AlwaysChange: !!step.alwaysChange,
          ...(transactionDate
            ? { TransactionDate: transactionDate.toISOString() }
            : {}),
        };

        console.log('updating positive transaction ===>', payload);

        await updateTransaction({
          transactionId: String(tx.WealthAccountTransactionId),
          transaction: payload,
        }).unwrap();
      }

      onRefreshStart?.();

      // Call onComplete if available (for chaining), otherwise onClose
      if (onComplete) {
        onComplete();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error updating transactions:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // TODO: Placeholder for transfer path logic - legacy behavior unclear
  // const handleTransferPath = () => {
  //     // PLACEHOLDER: Apply TransferCategoryId and advance to next
  //     console.log("Transfer path - TODO: Implement transfer flow logic");
  // };

  // // TODO: Implement rule creation logic for "always categorize"
  // const handleAlwaysChangeLogic = (alwaysChange: boolean) => {
  //     // PLACEHOLDER: Rule creation for future similar transactions
  //     console.log("Always change logic - TODO: Implement rule creation", { alwaysChange });
  // };

  if (!open) return null;

  const hasTransactions = positiveSpendingTransactions.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      sx={DIALOG_SX}
    >
      <DialogTitle>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box>
            <Typography variant='h6'>
              Positive Transactions Classified as Spending Wizard
            </Typography>

            {/* Chain progress indicator */}
            {isChaining && chainProgress && (
              <Typography
                variant='body2'
                color='primary'
                sx={{ fontWeight: 600 }}
              >
                Step {chainProgress.current} of {chainProgress.total}
              </Typography>
            )}

            {hasTransactions && !transactionsLoading && (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {isComplete
                  ? `All transactions reviewed (${steps.length})`
                  : `Transaction ${currentIndex + 1} of ${steps.length}`}
              </Typography>
            )}
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Loading state */}
        {transactionsLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant='h6'
              gutterBottom
            >
              Loading transactions...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* No transactions */}
        {!transactionsLoading && !hasTransactions && (
          <Alert severity='info'>
            No positive transactions classified as spending found for the
            selected date range.
          </Alert>
        )}

        {/* Saving state */}
        {!transactionsLoading && hasTransactions && isSaving && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant='h6'
              gutterBottom
            >
              Saving changes...
            </Typography>
            <LinearProgress />
            <Typography
              variant='body2'
              sx={{ mt: 2 }}
            >
              Updating {steps.filter((s) => s.action && !s.skipped).length}{' '}
              transactions
            </Typography>
          </Box>
        )}

        {/* Main content */}
        {!transactionsLoading && hasTransactions && !isSaving && (
          <>
            <Alert
              severity='info'
              sx={{ mb: 3 }}
            >
              {isComplete
                ? 'All transactions are reviewed.'
                : 'These transactions are positive amounts but currently categorized as spending. Help us correct their classification.'}
            </Alert>

            {/* Complete screen content */}
            {isComplete ? (
              <Box>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                >
                  Summary
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Paper
                    variant='outlined'
                    sx={{ p: 2, minWidth: 160 }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      Will be updated
                    </Typography>
                    <Typography variant='h6'>{editedCount}</Typography>
                  </Paper>

                  <Paper
                    variant='outlined'
                    sx={{ p: 2, minWidth: 160 }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      Skipped
                    </Typography>
                    <Typography variant='h6'>{skippedCount}</Typography>
                  </Paper>
                </Box>

                <Typography
                  variant='subtitle2'
                  sx={{ mb: 1 }}
                >
                  Updated Transactions
                </Typography>

                {editedSteps.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    No transactions will be updated.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      maxHeight: 280,
                      overflow: 'auto',
                      pr: 1,
                    }}
                  >
                    {editedSteps.map((s) => {
                      const tx = s.transaction;
                      const actionLabel =
                        s.action === 'income'
                          ? 'Income'
                          : s.action === 'transfer'
                            ? 'Transfer'
                            : 'Spending (Correct)';

                      return (
                        <Paper
                          key={tx.WealthAccountTransactionId}
                          variant='outlined'
                          sx={{ p: 1.5 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                noWrap
                              >
                                {tx.DisplayDescription}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {formatAppDate(tx.TransactionDate, {
                                  format: 'MMM DD, YYYY',
                                })}{' '}
                                • {tx.Name}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='primary'
                                sx={{ display: 'block' }}
                              >
                                → {actionLabel}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                              >
                                {formatNumberToCurrency(tx.Amount)}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {/* Transaction details */}
                {currentTransaction && (
                  <>
                    <Paper
                      elevation={1}
                      sx={{ p: 2, mb: 3 }}
                    >
                      <Typography
                        variant='subtitle1'
                        gutterBottom
                      >
                        Transaction Details
                      </Typography>

                      <Alert
                        severity='warning'
                        sx={{ mb: 2 }}
                      >
                        This transaction is positive but currently categorized
                        as spending.
                      </Alert>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            Description
                          </Typography>
                          <Typography
                            variant='body1'
                            fontWeight='medium'
                          >
                            {currentTransaction.DisplayDescription}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            Amount
                          </Typography>
                          <Typography
                            variant='body1'
                            fontWeight='medium'
                            color='success.main'
                          >
                            {formatNumberToCurrency(currentTransaction.Amount)}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            Date
                          </Typography>
                          <Typography variant='body1'>
                            {formatAppDate(currentTransaction.TransactionDate, {
                              format: 'MMM DD, YYYY',
                            })}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            Account
                          </Typography>
                          <Typography variant='body1'>
                            {currentTransaction.Name}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Action selection step */}
                    {currentStep === 'action' && (
                      <Box>
                        <Typography
                          variant='h6'
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <TrendingUp />
                          How should this transaction be classified?
                        </Typography>

                        <Typography
                          variant='body2'
                          color='text.secondary'
                          gutterBottom
                        >
                          Choose the correct classification for this positive
                          transaction.
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 3,
                          }}
                        >
                          <Button
                            variant='outlined'
                            size='large'
                            sx={{ justifyContent: 'flex-start', p: 2 }}
                            onClick={() => handleActionSelect('income')}
                          >
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography
                                variant='subtitle1'
                                fontWeight='bold'
                              >
                                Income
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                This should be classified as income
                              </Typography>
                            </Box>
                          </Button>

                          <Button
                            variant='outlined'
                            size='large'
                            sx={{ justifyContent: 'flex-start', p: 2 }}
                            onClick={() => handleActionSelect('transfer')}
                          >
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography
                                variant='subtitle1'
                                fontWeight='bold'
                              >
                                Transfer
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                This is a transfer between accounts
                              </Typography>
                            </Box>
                          </Button>

                          <Button
                            variant='outlined'
                            size='large'
                            sx={{ justifyContent: 'flex-start', p: 2 }}
                            onClick={() => handleActionSelect('spending')}
                          >
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography
                                variant='subtitle1'
                                fontWeight='bold'
                              >
                                Spending (This is correct)
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                This transaction is correctly categorized as
                                spending
                              </Typography>
                            </Box>
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {/* Income category selection step */}
                    {currentStep === 'income' && (
                      <Box>
                        <Typography
                          variant='h6'
                          gutterBottom
                        >
                          Select Income Category
                        </Typography>

                        <Typography
                          variant='body2'
                          color='text.secondary'
                          gutterBottom
                        >
                          Choose the appropriate income category for this
                          transaction.
                        </Typography>

                        <CategorySelector
                          label='Select Income Category'
                          value={selectedCategoryId ?? ''}
                          onChange={(id: number | '') => {
                            if (typeof id === 'number')
                              handleIncomeCategory(id);
                          }}
                          investorId={investorId}
                        />

                        {selectedCategoryId && (
                          <Box sx={{ mt: 3 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={alwaysChange}
                                  onChange={(e) =>
                                    setAlwaysChange(e.target.checked)
                                  }
                                />
                              }
                              label='Always categorize future transactions with this description the same way.'
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        {/* If loading / no transactions / saving, keep actions minimal */}
        {transactionsLoading || !hasTransactions || isSaving ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            {/* Back */}
            {(currentIndex > 0 || currentStep === 'income' || isComplete) && (
              <Button
                startIcon={<ArrowBack />}
                onClick={moveBack}
                disabled={isSaving}
              >
                Back
              </Button>
            )}

            {/* Skip (only while reviewing and on action step) */}
            {!isComplete && currentStep === 'action' && (
              <Button
                variant='outlined'
                onClick={handleSkip}
                disabled={isSaving}
              >
                Skip
              </Button>
            )}

            {/* Next button for income step */}
            {!isComplete && currentStep === 'income' && (
              <Button
                variant='contained'
                onClick={handleIncomeComplete}
                disabled={!selectedCategoryId || isSaving}
              >
                {currentIndex === steps.length - 1 ? 'Review' : 'Next'}
              </Button>
            )}

            {/* Finish button */}
            {resolvedTransactions > 0 && (
              <Button
                variant='contained'
                onClick={handleFinish}
                disabled={isSaving}
              >
                {isChaining &&
                chainProgress &&
                chainProgress.current < chainProgress.total
                  ? 'Finish & Next'
                  : 'Finish'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PositiveTransactionWizard;
